import type { TypedSupabaseClient } from '../client';
import type {
  IUserProfileRepository,
  UpdateUserProfileInput,
  UserProfile,
} from '../../../interfaces/src';

/**
 * Profil du compte connecté (`user_profiles`).
 *
 * Accès direct à la table, pas de RPC : la RLS suffit (lecture = soi + les
 * personnes avec qui on partage une société, écriture = soi seul), et il n'y a
 * rien à joindre dans `auth.*` — contrairement au registre d'appareils.
 *
 * Table ancrée sur l'UTILISATEUR : elle vit sur le client `master`, jamais sur
 * le client tenant. Elle n'a d'ailleurs pas de colonne `tenant_id`.
 */

interface ProfileRow {
  user_id: string;
  display_name: string | null;
  phone: string | null;
  job_title: string | null;
  avatar_url: string | null;
  cover_url: string | null;
  created_at: string;
  updated_at: string;
}

function mapProfile(row: ProfileRow): UserProfile {
  return {
    userId: row.user_id,
    displayName: row.display_name,
    phone: row.phone,
    jobTitle: row.job_title,
    avatarUrl: row.avatar_url,
    coverUrl: row.cover_url,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export class SupabaseUserProfileRepository implements IUserProfileRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getMine(): Promise<UserProfile | null> {
    const { data: { user } } = await this.supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await this.supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) throw error;
    return data ? mapProfile(data as ProfileRow) : null;
  }

  async updateMine(input: UpdateUserProfileInput): Promise<UserProfile> {
    const { data: { user } } = await this.supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // On ne monte QUE les colonnes réellement fournies : l'upsert PostgREST
    // ne met à jour que les colonnes présentes dans la charge utile. Envoyer
    // l'objet complet ferait effacer l'avatar dès qu'on enregistre le simple
    // formulaire d'identité (les deux écrivent la même ligne).
    const payload: Record<string, unknown> = { user_id: user.id };
    if ('displayName' in input) payload['display_name'] = input.displayName ?? null;
    if ('phone' in input) payload['phone'] = input.phone ?? null;
    if ('jobTitle' in input) payload['job_title'] = input.jobTitle ?? null;
    if ('avatarUrl' in input) payload['avatar_url'] = input.avatarUrl ?? null;
    if ('coverUrl' in input) payload['cover_url'] = input.coverUrl ?? null;

    const { data, error } = await this.supabase
      .from('user_profiles')
      .upsert(payload as never, { onConflict: 'user_id' })
      .select('*')
      .single();

    if (error) throw error;
    return mapProfile(data as ProfileRow);
  }
}
